/**
 * The rate-limit state an answer describes.
 *
 * The Data API and the Tile API describe the limits an answer was counted against, on every answer
 * the limits weighed, under a key and on the Tile API under a share token as well, in the two
 * fields the IETF's RateLimit header fields for HTTP define: `RateLimit-Policy` lists the
 * policies, each a String member naming one with its quota `q` and its window `w` in seconds, and
 * `RateLimit` states what is left of each, `r`, and the seconds until its window resets, `t`. The
 * Map Studio API sends neither.
 *
 * Both are Structured Field Lists (RFC 9651), so they are parsed as one rather than split on
 * commas: a quoted name may hold a comma, a parameter may hold a byte sequence, and a field that
 * does not parse is ignored whole, as the RFC has a recipient do. The state is reported and never
 * counted against: a client delays no request on what it says, since an organization's figures
 * are its agreement's, which may raise, lower or remove them.
 */

/**
 * RateLimitPolicy is one limit an answer was counted against. `minute`, a limit per minute, and
 * `month`, a monthly allowance where an agreement sets one, hold the organization's own clients
 * and, on the Tile API, Mountaya's hosted embed. `address` is the bound on one calling address: on
 * the Tile API a key sent with no session token, the hosted embed and a share token are held to
 * it, and on both APIs a session token minted for `studio-embedded` naming nobody. `member` and
 * `studio-embedded` are the bounds a session token minted for `studio-embedded` naming a member is
 * held to, its member's and its organization's. `share` is the bound every share link of one
 * organization shares, on the Tile API.
 */
export interface RateLimitPolicy {
  /** The policy's name, as `RateLimit-Policy` names it. */
  readonly name: string;
  /** The requests the policy allows per window: its `q`, a whole number of zero or more. */
  readonly quota: number;
  /** What is left of the quota in the current window, where the answer states it. */
  readonly remaining?: number | undefined;
  /** The seconds until the window resets, where the answer states it. */
  readonly resetSeconds?: number | undefined;
  /** The window's length in seconds, where the policy states it. */
  readonly windowSeconds?: number | undefined;
}

/**
 * RateLimitInfo is the rate-limit state an answer describes: one policy per member of its
 * `RateLimit-Policy`, in the order the field lists them.
 */
export interface RateLimitInfo {
  /** The policies the answer was counted against, never empty. */
  readonly policies: readonly RateLimitPolicy[];
}

/** One parameter's value, or an item's: the bare items RFC 9651 knows, by kind. */
type BareItem =
  | { readonly kind: "boolean"; readonly value: boolean }
  | { readonly kind: "bytes" }
  | { readonly kind: "date"; readonly value: number }
  | { readonly kind: "decimal"; readonly value: number }
  | { readonly kind: "display"; readonly value: string }
  | { readonly kind: "integer"; readonly value: number }
  | { readonly kind: "string"; readonly value: string }
  | { readonly kind: "token"; readonly value: string };

/** One member of a List: an item or an inner list, with its parameters. */
interface Member {
  /** The member's bare item, or none for an inner list. */
  readonly item: BareItem | undefined;
  /** The member's parameters, by key; a key written twice keeps the last value. */
  readonly parameters: ReadonlyMap<string, BareItem>;
}

const digitPattern = /[0-9]/;
const alphaPattern = /[A-Za-z]/;
const lowercaseAlphaPattern = /[a-z]/;
const keyCharPattern = /[a-z0-9_.*-]/;
// A token's characters past its first: tchar, ":" and "/".
const tokenCharPattern = /[!#$%&'*+\-.^_`|~0-9A-Za-z:/]/;
const base64CharPattern = /[A-Za-z0-9+/=]/;

/**
 * The parser of RFC 9651 section 4.2, over one field value, as a cursor over its characters. Each
 * method consumes what it parses and answers undefined where the input is no Structured Field,
 * which {@link parseList} turns into no list at all.
 */
class Cursor {
  private index = 0;

  constructor(private readonly text: string) {}

  /** Whether every character is consumed. */
  get done(): boolean {
    return this.index >= this.text.length;
  }

  /**
   * The next character, or "" at the end. A method rather than a getter, so a comparison with it
   * is not taken to hold after the cursor moves.
   */
  private peek(): string {
    return this.text[this.index] ?? "";
  }

  /** Consumes one character and answers it. */
  private take(): string {
    const char = this.peek();
    this.index += 1;

    return char;
  }

  /** Discards spaces. */
  skipSpaces(): void {
    while (this.peek() === " ") {
      this.index += 1;
    }
  }

  /** Discards optional whitespace: spaces and tabs. */
  skipWhitespace(): void {
    while (this.peek() === " " || this.peek() === "\t") {
      this.index += 1;
    }
  }

  /** Consumes `char` and answers whether the next character was it. */
  consume(char: string): boolean {
    return this.take() === char;
  }

  /** An item or an inner list, and its parameters. */
  member(): Member | undefined {
    if (this.peek() === "(") {
      if (!this.innerList()) {
        return undefined;
      }

      const parameters = this.parameters();

      return parameters === undefined ? undefined : { item: undefined, parameters };
    }

    const item = this.bareItem();
    const parameters = item === undefined ? undefined : this.parameters();

    return parameters === undefined ? undefined : { item, parameters };
  }

  /** An inner list, `(item item);params`, whose items are parsed and set aside. */
  private innerList(): boolean {
    this.index += 1;
    for (;;) {
      this.skipSpaces();
      if (this.peek() === ")") {
        this.index += 1;

        return true;
      }

      if (this.bareItem() === undefined || this.parameters() === undefined) {
        return false;
      }

      if (this.peek() !== " " && this.peek() !== ")") {
        return false;
      }
    }
  }

  /** The parameters after an item: `;key=value`, a key alone being `true`. */
  private parameters(): Map<string, BareItem> | undefined {
    const parameters = new Map<string, BareItem>();

    while (this.peek() === ";") {
      this.index += 1;
      this.skipSpaces();
      const key = this.key();
      if (key === undefined) {
        return undefined;
      }

      let value: BareItem | undefined = { kind: "boolean", value: true };
      if (this.peek() === "=") {
        this.index += 1;
        value = this.bareItem();
      }

      if (value === undefined) {
        return undefined;
      }

      parameters.set(key, value);
    }

    return parameters;
  }

  /** A parameter's key: a lowercase letter or `*`, then lowercase letters, digits, `_-.*`. */
  private key(): string | undefined {
    if (!lowercaseAlphaPattern.test(this.peek()) && this.peek() !== "*") {
      return undefined;
    }

    let key = this.take();
    while (keyCharPattern.test(this.peek())) {
      key += this.take();
    }

    return key;
  }

  /** One bare item, its kind decided by its first character. */
  private bareItem(): BareItem | undefined {
    const first = this.peek();

    if (first === "-" || digitPattern.test(first)) {
      return this.number();
    }

    if (first === '"') {
      const value = this.string();

      return value === undefined ? undefined : { kind: "string", value };
    }

    if (first === "*" || alphaPattern.test(first)) {
      return { kind: "token", value: this.token() };
    }

    if (first === ":") {
      return this.bytes() ? { kind: "bytes" } : undefined;
    }

    if (first === "?") {
      this.index += 1;
      const value = this.take();

      return value === "0" || value === "1" ? { kind: "boolean", value: value === "1" } : undefined;
    }

    if (first === "@") {
      this.index += 1;
      const date = this.number();

      return date?.kind === "integer" ? { kind: "date", value: date.value } : undefined;
    }

    if (first === "%") {
      this.index += 1;
      const value = this.displayString();

      return value === undefined ? undefined : { kind: "display", value };
    }

    return undefined;
  }

  /** An integer of at most 15 digits, or a decimal of at most 12 before its point and 3 after. */
  private number(): BareItem | undefined {
    let sign = 1;
    if (this.peek() === "-") {
      sign = -1;
      this.index += 1;
    }

    let digits = "";
    let decimal = false;
    while (digitPattern.test(this.peek()) || (this.peek() === "." && !decimal)) {
      if (this.peek() === ".") {
        if (digits.length > 12) {
          return undefined;
        }

        decimal = true;
      }

      digits += this.take();
      if ((!decimal && digits.length > 15) || (decimal && digits.length > 16)) {
        return undefined;
      }
    }

    if (digits === "" || digits.startsWith(".")) {
      return undefined;
    }

    if (!decimal) {
      return { kind: "integer", value: sign * Number(digits) };
    }

    const fraction = digits.slice(digits.indexOf(".") + 1);
    if (fraction.length === 0 || fraction.length > 3) {
      return undefined;
    }

    return { kind: "decimal", value: sign * Number(digits) };
  }

  /** A quoted string of printable ASCII, whose only escapes are `\"` and `\\`. */
  private string(): string | undefined {
    this.index += 1;
    let value = "";

    for (;;) {
      if (this.done) {
        return undefined;
      }

      const char = this.take();
      if (char === '"') {
        return value;
      }

      if (char === "\\") {
        const escaped = this.take();
        if (escaped !== '"' && escaped !== "\\") {
          return undefined;
        }

        value += escaped;
        continue;
      }

      const code = char.charCodeAt(0);
      if (code < 0x20 || code > 0x7e) {
        return undefined;
      }

      value += char;
    }
  }

  /** A token: a letter or `*`, then tchar, `:` and `/`. */
  private token(): string {
    let value = this.take();
    while (tokenCharPattern.test(this.peek())) {
      value += this.take();
    }

    return value;
  }

  /** A byte sequence, `:base64:`, read for its shape alone. */
  private bytes(): boolean {
    this.index += 1;
    while (base64CharPattern.test(this.peek())) {
      this.index += 1;
    }

    return this.consume(":");
  }

  /** A display string, `"…"` with each byte past ASCII percent-encoded as lowercase hex. */
  private displayString(): string | undefined {
    if (!this.consume('"')) {
      return undefined;
    }

    const bytes: number[] = [];

    for (;;) {
      if (this.done) {
        return undefined;
      }

      const char = this.take();
      const code = char.charCodeAt(0);
      if (code < 0x20 || code > 0x7e) {
        return undefined;
      }

      if (char === '"') {
        try {
          return new TextDecoder("utf-8", { fatal: true }).decode(Uint8Array.from(bytes));
        } catch {
          // Bytes that are not UTF-8, which no display string carries.
          return undefined;
        }
      }

      if (char === "%") {
        const hex = this.take() + this.take();
        if (!/^[0-9a-f]{2}$/.test(hex)) {
          return undefined;
        }

        bytes.push(Number.parseInt(hex, 16));
        continue;
      }

      bytes.push(code);
    }
  }
}

/**
 * parseList parses one field value as a Structured Field List, or answers undefined where it is
 * none: RFC 9651 has a recipient ignore such a field whole.
 */
const parseList = (text: string): readonly Member[] | undefined => {
  const cursor = new Cursor(text);
  const members: Member[] = [];

  cursor.skipSpaces();
  while (!cursor.done) {
    const member = cursor.member();
    if (member === undefined) {
      return undefined;
    }

    members.push(member);
    cursor.skipWhitespace();
    if (cursor.done) {
      break;
    }

    if (!cursor.consume(",")) {
      return undefined;
    }

    cursor.skipWhitespace();
    // A trailing comma is no list.
    if (cursor.done) {
      return undefined;
    }
  }

  return members;
};

/** A parameter's value where it is a whole number of zero or more, and undefined otherwise. */
const wholeNumber = (item: BareItem | undefined): number | undefined =>
  item?.kind === "integer" && item.value >= 0 ? item.value : undefined;

/** The String a member names its policy by, or undefined where it is no String. */
const policyName = (member: Member): string | undefined =>
  member.item?.kind === "string" ? member.item.value : undefined;

/**
 * parseRateLimit reads the rate-limit state an answer's headers describe, or undefined where they
 * describe none.
 *
 * One policy per String member of `RateLimit-Policy`, in the order it lists them, each with its
 * name, its quota `q` and its window `w`, and with the remaining figure `r` and the reset `t` of
 * the `RateLimit` member of the same name. A figure the answer does not state is undefined. A
 * member that is no String, or whose `q` is no whole number of zero or more, is left out, and so
 * is a `RateLimit` member naming no policy the other field lists, since its quota is unknown; a
 * parameter this client does not know is ignored; and a field that does not parse as a Structured
 * Field List is ignored whole. An answer whose `RateLimit-Policy` leaves no policy describes none.
 *
 * @param headers - The answer's headers, as a `Response` carries them.
 * @returns The state, or undefined where the answer describes no policy.
 *
 * @example
 * ```ts
 * import { parseRateLimit } from "@mountaya/client";
 *
 * declare const publishableKey: string;
 * declare const sessionToken: string;
 *
 * // An answer made under a key, which the Data API describes the limits of.
 * const response = await fetch("https://data.mountaya.com/graphql", {
 *   body: JSON.stringify({ query: "query { __typename }" }),
 *   headers: {
 *     "Content-Type": "application/json",
 *     "X-API-Key": publishableKey,
 *     "X-Session-Token": sessionToken,
 *   },
 *   method: "POST",
 * });
 * const minute = parseRateLimit(response.headers)?.policies.find(({ name }) => name === "minute");
 * ```
 */
export const parseRateLimit = (headers: Pick<Headers, "get">): RateLimitInfo | undefined => {
  const policyField = headers.get("RateLimit-Policy");
  const declared = policyField === null ? undefined : parseList(policyField);
  if (declared === undefined) {
    return undefined;
  }

  const stateField = headers.get("RateLimit");
  const states = new Map<string, Member>();
  for (const member of (stateField === null ? undefined : parseList(stateField)) ?? []) {
    const name = policyName(member);
    if (name !== undefined) {
      states.set(name, member);
    }
  }

  const policies: RateLimitPolicy[] = [];
  for (const member of declared) {
    const name = policyName(member);
    const quota = wholeNumber(member.parameters.get("q"));
    if (name === undefined || quota === undefined) {
      continue;
    }

    const state = states.get(name)?.parameters;
    policies.push({
      name,
      quota,
      remaining: wholeNumber(state?.get("r")),
      resetSeconds: wholeNumber(state?.get("t")),
      windowSeconds: wholeNumber(member.parameters.get("w")),
    });
  }

  return policies.length === 0 ? undefined : { policies };
};
